import { getRecordValue, isRecord } from '@/lib/utils/type-guards';

export type SweetLinkStatus = 'idle' | 'connecting' | 'connected' | 'error';

export interface SweetLinkStatusSnapshot {
  codename: string | null;
  reason: string | null;
  status: SweetLinkStatus;
}

type StoredSessionPayload = {
  snapshot: SweetLinkStatusSnapshot;
  timestamp: number;
};

export const DEFAULT_SWEETLINK_STATUS_SNAPSHOT: SweetLinkStatusSnapshot = {
  status: 'idle',
  reason: null,
  codename: null,
};

function resolveWindow(): Window | undefined {
  if (globalThis.window === undefined) {
    return;
  }
  return globalThis.window;
}

declare global {
  interface Window {
    __sweetlinkStatusSnapshot?: SweetLinkStatusSnapshot;
  }
}

const STORED_SESSION_KEY = 'sweetistics:sweetlink-session';
const STORED_SESSION_STALE_MS = 15 * 60 * 1000;

const isSweetLinkStatus = (value: unknown): value is SweetLinkStatus =>
  value === 'idle' || value === 'connecting' || value === 'connected' || value === 'error';

const normalizeSnapshot = (snapshot: unknown): SweetLinkStatusSnapshot | null => {
  if (!isRecord(snapshot)) {
    return null;
  }
  const status = getRecordValue(snapshot, 'status');
  if (!isSweetLinkStatus(status)) {
    return null;
  }
  const reasonValue = getRecordValue(snapshot, 'reason');
  const codenameValue = getRecordValue(snapshot, 'codename');
  const reason = typeof reasonValue === 'string' ? reasonValue : null;
  const codename = typeof codenameValue === 'string' ? codenameValue : null;
  return { status, reason, codename };
};

const persistSessionSnapshot = (snapshot: SweetLinkStatusSnapshot): void => {
  try {
    if (snapshot.status === 'connected') {
      const payload: StoredSessionPayload = { snapshot, timestamp: Date.now() };
      globalThis.sessionStorage?.setItem(STORED_SESSION_KEY, JSON.stringify(payload));
      return;
    }
    globalThis.sessionStorage?.removeItem(STORED_SESSION_KEY);
  } catch {
    /* ignore storage failures */
  }
};

export const getStoredSweetLinkSession = (): StoredSessionPayload | null => {
  try {
    const raw = globalThis.sessionStorage?.getItem(STORED_SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return null;
    }
    const timestampValue = getRecordValue(parsed, 'timestamp');
    const snapshotValue = getRecordValue(parsed, 'snapshot');
    const timestamp = typeof timestampValue === 'number' ? timestampValue : null;
    const snapshot = normalizeSnapshot(snapshotValue);
    if (!(timestamp && snapshot)) {
      return null;
    }
    if (Date.now() - timestamp > STORED_SESSION_STALE_MS) {
      globalThis.sessionStorage?.removeItem(STORED_SESSION_KEY);
      return null;
    }
    return { timestamp, snapshot };
  } catch {
    return null;
  }
};

export function setSweetLinkStatusSnapshot(snapshot: SweetLinkStatusSnapshot): void {
  const sweetlinkWindow = resolveWindow();
  if (!sweetlinkWindow) {
    return;
  }
  sweetlinkWindow.__sweetlinkStatusSnapshot = snapshot;
  persistSessionSnapshot(snapshot);
}

export function getSweetLinkStatusSnapshot(): SweetLinkStatusSnapshot {
  const sweetlinkWindow = resolveWindow();
  if (!sweetlinkWindow) {
    return DEFAULT_SWEETLINK_STATUS_SNAPSHOT;
  }
  return sweetlinkWindow.__sweetlinkStatusSnapshot ?? DEFAULT_SWEETLINK_STATUS_SNAPSHOT;
}
